import { useEffect } from 'react';

type SeoProps = {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  jsonLd?: object | object[];
};

/**
 * Componente headless que actualiza title + meta tags + JSON-LD por página.
 * No renderiza nada. Cleanup automático en unmount.
 */
export function Seo({
  title,
  description,
  keywords,
  canonical,
  ogImage = 'https://nuled.mx/og-default.jpg',
  jsonLd,
}: SeoProps) {
  useEffect(() => {
    document.title = title;
    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    // OpenGraph
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('og:image', ogImage, 'property');
    if (typeof window !== 'undefined') {
      setMeta('og:url', window.location.href, 'property');
    }

    // Twitter
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', ogImage);

    // Canonical
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonical;
    }

    // JSON-LD
    const jsonLdNodes: HTMLScriptElement[] = [];
    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((obj, i) => {
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.text = JSON.stringify(obj);
        s.dataset.seo = `page-${i}`;
        document.head.appendChild(s);
        jsonLdNodes.push(s);
      });
    }

    return () => {
      jsonLdNodes.forEach(n => n.remove());
    };
  }, [title, description, keywords, canonical, ogImage, JSON.stringify(jsonLd)]);

  return null;
}

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}

// Helpers para JSON-LD comunes
export const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'NULED',
  alternateName: 'NULED Iluminación LED',
  url: 'https://nuled.mx',
  logo: 'https://nuled.mx/logo.svg',
  description: 'Distribuidor mayorista de iluminación LED y educador Lutron en México.',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'MX',
  },
  sameAs: ['https://instagram.com/nuled.mx'],
};

export function productJsonLd(p: {
  name: string;
  sku?: string;
  model?: string;
  description?: string | null;
  image?: string;
  msrp_mxn?: number | null;
  brand_code: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: p.name,
    sku: p.sku,
    model: p.model,
    description: p.description?.replace(/<[^>]+>/g, '').slice(0, 280) ?? '',
    image: p.image ?? undefined,
    brand: {
      '@type': 'Brand',
      name: p.brand_code === 'lutron' ? 'Lutron' : 'NULED',
    },
    offers:
      p.msrp_mxn != null
        ? {
            '@type': 'Offer',
            priceCurrency: 'MXN',
            price: p.msrp_mxn,
            availability: 'https://schema.org/InStock',
            url: typeof window !== 'undefined' ? window.location.href : undefined,
          }
        : undefined,
  };
}

export function faqJsonLd(items: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(i => ({
      '@type': 'Question',
      name: i.q,
      acceptedAnswer: { '@type': 'Answer', text: i.a },
    })),
  };
}

export function breadcrumbJsonLd(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: it.name,
      item: it.url,
    })),
  };
}
